import { createContext } from "@lit/context";
import type { EmbeddedApiClients } from "../data/embedded-api";
import type { CheckoutGateway } from "../checkout/actors";

export interface CustomerIdentitySeed {
  userId: number;
  rosterId: number;
  displayName?: string;
}

export interface EmbedCompletion {
  kind: "booking" | "registration" | "purchase";
  orderId?: string;
  intentId?: string;
  correlationId?: string;
  canonicalResourceKeys?: readonly string[];
}

export interface EmbedRuntime {
  surface: "public-embed" | "dashboard-customer";
  /** Generated OpenAPI clients supplied by the host. Public embeds omit this
   * so each call resolves the current API base and customer JWT dynamically. */
  api?: EmbeddedApiClients;
  /** Surface-specific commerce transport. Components never switch endpoints. */
  commerce?: CheckoutGateway;
  /** Stable scope used to bind public capabilities and dashboard actors. */
  audience?: string;
  /** Changes whenever host-side viewer/relationship authority changes. */
  viewerRevision?: string;
  locale?: string;
  timezone?: string;
  customer?: CustomerIdentitySeed;
  onComplete?: (completion: EmbedCompletion) => void | Promise<void>;
}

export const embedRuntimeContext = createContext<EmbedRuntime>(
  Symbol.for("zipper.embed.runtime"),
);

export function createPublicEmbedRuntime(
  overrides: Partial<Omit<EmbedRuntime, "surface">> = {},
): EmbedRuntime {
  if (overrides.api && overrides.api.surface !== "public-embed") {
    throw new TypeError("Public runtime requires public-embed API clients");
  }
  return {
    surface: "public-embed",
    ...(overrides.api ? { api: overrides.api } : {}),
    ...(overrides.commerce ? { commerce: overrides.commerce } : {}),
    ...(overrides.locale ? { locale: overrides.locale } : {}),
    ...(overrides.timezone ? { timezone: overrides.timezone } : {}),
    ...(overrides.customer ? { customer: overrides.customer } : {}),
    ...(overrides.audience ? { audience: overrides.audience } : {}),
    ...(overrides.viewerRevision
      ? { viewerRevision: overrides.viewerRevision }
      : {}),
    ...(overrides.onComplete ? { onComplete: overrides.onComplete } : {}),
  };
}

export function createDashboardCustomerRuntime(options: {
  api: EmbeddedApiClients;
  commerce?: CheckoutGateway;
  audience?: string;
  viewerRevision?: string;
  locale?: string;
  timezone?: string;
  customer?: CustomerIdentitySeed;
  onComplete?: (completion: EmbedCompletion) => void | Promise<void>;
}): EmbedRuntime {
  if (options.api.surface !== "dashboard-customer") {
    throw new TypeError(
      "Dashboard runtime requires dashboard-customer API clients",
    );
  }
  return {
    surface: "dashboard-customer",
    api: options.api,
    ...(options.commerce ? { commerce: options.commerce } : {}),
    ...(options.locale ? { locale: options.locale } : {}),
    ...(options.timezone ? { timezone: options.timezone } : {}),
    ...(options.customer ? { customer: options.customer } : {}),
    ...(options.audience ? { audience: options.audience } : {}),
    ...(options.viewerRevision
      ? { viewerRevision: options.viewerRevision }
      : {}),
    ...(options.onComplete ? { onComplete: options.onComplete } : {}),
  };
}

/**
 * Preserve the public embed's current call shape while allowing dashboard
 * hosts to inject their generated clients into existing positional functions.
 */
export function runtimeApiArguments(
  runtime: EmbedRuntime,
): [] | [EmbeddedApiClients] {
  return runtime.api ? [runtime.api] : [];
}

/** Same bridge for request parameter objects. */
export function runtimeApiOption(
  runtime: EmbedRuntime,
): Record<never, never> | { api: EmbeddedApiClients } {
  return runtime.api ? { api: runtime.api } : {};
}
